import React, { useEffect, useState, Fragment } from 'react';
import { Box, List, ListItem, ListItemText, ListSubheader, ListItemIcon, Divider, Typography, Collapse } from '@material-ui/core';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory, useLocation, useRouteMatch } from 'react-router';

import { DrawerWrapper } from 'src/components';
import { urlJoin, truncateWithEllipsis as twe } from 'src/utils/util';
import { ChartBar, FormatListGroup /*, Water */ } from 'src/utils/mdIcons';


const useStyles = makeStyles(theme => ({
  titleBox: {
    height: '64px',
    textOverflow: 'ellipsis',
  },
  title: {
    color: theme.palette.common.gray800,
    lineHeight: 1.25,
    fontWeight: 500,
  },
  overTitle: {
    lineHeight: 1,
  },
  listItemSelected: {
    backgroundColor: `${theme.palette.secondary.main} !important`,
    color: theme.palette.common.white,
  },
  nested: {
    paddingLeft: theme.spacing(4),
  },
  listItem: {
    borderRadius: '6px',
  },
  list: {
    paddingRight: theme.spacing(1),
    paddingLeft: theme.spacing(1),
  },
  listItemIcon: {
    minWidth: '36px',
  },
  sectionHeader: {
    lineHeight: 0,
  },
  paperRoot: {
    marginLeft: '97px',
  },
  iconSelected: {
    color: theme.palette.common.white,
  },
}));

const itemList = [
  {
    Icon: ChartBar,
    label: 'Analíticas',
    endPath: 'analytics',
    sectionName: 'Gestión de datos',
  },
  {
    Icon: FormatListGroup,
    label: 'Grupos',
    endPath: 'groups',
  },
];

const getIndex = currentPathname => {
  const lastString = currentPathname.split('/').pop();
  const index = itemList.findIndex(item => item.endPath === lastString);
  let idx;
  if (index === -1) {
    itemList.forEach((item, index) => {
      if (item.subItems) {
        const subIndex = item.subItems.findIndex(si => si.endPath === lastString);
        if (subIndex !== -1) {
          idx = `${index}-${subIndex}`;
        }
      }
    });
  } else {
    idx = index;
  }
  return idx;
};

const MonitoringPlanDrawer = ({ name, userRoleId }) => {
  const classes = useStyles();
  const history = useHistory();
  const match = useRouteMatch();
  const { pathname } = useLocation();
  const startIndex = getIndex(pathname);

  const [ selectedIndex, setSelectedIndex ] = useState(startIndex);

  const [ open, setOpen ] = useState(startIndex && typeof startIndex !== 'number' ? { [startIndex.split('-')[0]]: true } : {});

  const { projectId, siteId, monitoringPlanId } = match.params;

  const baseUrl = `/web/projects/${projectId}${siteId ? `/sites/${siteId}` : ''}/monitoring-plans/${monitoringPlanId}`;

  const handleSelectedIndex = index => () => {
    if (itemList[index]?.disabled) {
      return;
    }
    if (itemList[index]?.subItems) {
      setOpen(prev => ({ ...prev, [index]: !prev[index] }));
    } else {
      let targetUrl;
      if (typeof index === 'number') {
        targetUrl = urlJoin(baseUrl, itemList[index].endPath);
      } else {
        const [ str1, str2 ] = index.split('-');
        targetUrl = urlJoin(baseUrl, itemList[str1].endPath, itemList[str1].subItems[str2].endPath);
        setSelectedIndex({ index });
      }
      history.push(targetUrl);
    }
  };

  useEffect(() => {
    setSelectedIndex(getIndex(pathname));
  }, [ pathname ]);

  const commonListItemProps = { button: true, component: 'a', classes: { root: classes.listItem, selected: classes.listItemSelected } };

  const filteredItemList = itemList.filter(({ allowOnly }) => !allowOnly || allowOnly.includes(userRoleId));

  return (
    <DrawerWrapper anchor="left" PaperProps={{ classes: { root: classes.paperRoot } }}>
      <Box mt={7}>
        <Box my={5} />
        <Box ml={2} display="flex" alignItems="center">
          <Box px={1} display="flex" flexDirection="column" justifyContent="center" className={classes.titleBox}>
            <Typography variant="caption" className={classes.overTitle}>Plan de monitoreo</Typography>
            <Typography className={classes.title} variant="body1">{twe(name, 70)}</Typography>
          </Box>
        </Box>
        <List className={classes.list}>
          {filteredItemList.map(({ Icon, label, subItems, sectionName, isCollapsable }, itemIndex) => (
            <Fragment key={itemIndex}>
              {sectionName &&
                <>
                  <Box {...(itemIndex === 0 ? { pb: 2 } : { py: 2 })} mx={-1}>
                    <Divider />
                  </Box>
                  <Box py={2}>
                    <ListSubheader className={classes.sectionHeader} color="primary">
                      {sectionName}
                    </ListSubheader>
                  </Box>
                </>
              }
              <ListItem selected={selectedIndex === itemIndex} onClick={handleSelectedIndex(itemIndex)} {...commonListItemProps}>
                <ListItemIcon classes={{ root: classes.listItemIcon }}>
                  <Icon className={selectedIndex === itemIndex ? classes.iconSelected : ''} />
                </ListItemIcon>
                <ListItemText primary={label} />
              </ListItem>
              {subItems &&
                  <Collapse in={isCollapsable ? open[itemIndex] : true} timeout="auto" unmountOnExit>
                    <List className={classes.list} disablePadding>
                      {subItems.map(({ Icon, label }, subitemIndex) =>
                        <ListItem key={`${itemIndex}-${subitemIndex}`} selected={selectedIndex === `${itemIndex}-${subitemIndex}`}
                          onClick={handleSelectedIndex(`${itemIndex}-${subitemIndex}`)} button
                          className={classes.nested} {...commonListItemProps}>
                          <ListItemIcon classes={{ root: classes.listItemIcon }}>
                            <Icon className={selectedIndex === itemIndex ? classes.iconSelected : ''} />
                          </ListItemIcon>
                          <ListItemText primary={label} />
                        </ListItem>,
                      )}
                    </List>
                  </Collapse>
              }
            </Fragment>
          ))}
        </List>
      </Box>
    </DrawerWrapper>
  );
};

MonitoringPlanDrawer.propTypes = {
  name: PropTypes.string,
  userRoleId: PropTypes.oneOf([ 'admin', 'user', 'consultant' ]),
};


export { MonitoringPlanDrawer };
